import { useTheme } from "@/hooks/useTheme";

const steps = [
  {
    number: "01",
    title: "Every Interaction Captured",
    description: "AI-powered call handling and lead capture ensures zero missed opportunities — 24/7, across every channel.",
    icon: "ri-phone-line",
    image: "https://public.readdy.ai/ai/img_res/0404a2a68c2e1d130d9e15594461db17.jpg",
    accentColor: "#E8761A",
  },
  {
    number: "02",
    title: "Instantly Processed",
    description: "Smart scheduling and workflow automation routes every lead, booking, and inquiry to the right place in real time.",
    icon: "ri-cpu-line",
    image: "https://readdy.ai/api/search-image?query=automated%20workflow%20processing%20digital%20nodes%20connected%20pathways%20dark%20background%20glowing%20circuits%20minimal%20technology%20abstract%20clean&width=500&height=350&seq=how002&orientation=landscape",
    accentColor: "#F5C842",
  },
  {
    number: "03",
    title: "Seamlessly Integrated",
    description: "CRM and platform integration unifies your entire operation — from first contact to final conversion.",
    icon: "ri-links-line",
    image: "https://readdy.ai/api/search-image?query=CRM%20system%20integration%20connected%20platforms%20unified%20dashboard%20dark%20background%20glowing%20interface%20minimal%20technology%20abstract%20data&width=500&height=350&seq=how003&orientation=landscape",
    accentColor: "#5A7BA8",
  },
  {
    number: "04",
    title: "Automatically Followed Up",
    description: "Automated follow-ups, reminders, and reactivation campaigns keep every opportunity moving forward.",
    icon: "ri-notification-3-line",
    image: "https://readdy.ai/api/search-image?query=automated%20follow%20up%20notification%20system%20digital%20messages%20flowing%20dark%20background%20minimal%20clean%20technology%20abstract%20glowing%20interface&width=500&height=350&seq=how004&orientation=landscape",
    accentColor: "#E8761A",
  },
];

export default function HowItWorksSection() {
  const { isDark } = useTheme();

  const bg = isDark ? "#1C1A22" : "#FBF6EE";
  const headlineColor = isDark ? "white" : "#1C1A22";
  const mutedColor = isDark ? "rgba(255,255,255,0.38)" : "rgba(28,26,34,0.45)";
  const cardBorder = isDark ? "rgba(255,255,255,0.06)" : "rgba(28,26,34,0.09)";
  const contentBg = isDark ? "rgba(255,255,255,0.02)" : "rgba(255,255,255,0.5)";
  const dividerGradient = isDark
    ? "linear-gradient(90deg, transparent, rgba(232,118,26,0.3), rgba(245,200,66,0.2), transparent)"
    : "linear-gradient(90deg, transparent, rgba(232,118,26,0.4), rgba(245,200,66,0.3), transparent)";

  return (
    <section id="how-it-works" className="py-28 md:py-36 relative overflow-hidden transition-colors duration-500" style={{ background: bg }}>
      <div className="max-w-7xl mx-auto px-6 md:px-12 mb-20">
        <div className="h-px w-full" style={{ background: dividerGradient }} />
      </div>

      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="text-center mb-20">
          <p className="text-xs uppercase tracking-[0.25em] font-medium mb-5" style={{ color: "#E8761A" }}>How It Works</p>
          <h2 className="text-4xl md:text-6xl font-thin leading-tight mb-6 tracking-tight transition-colors duration-500" style={{ color: headlineColor }}>
            From First Contact to
            <br />
            <span className="font-semibold">Final Conversion.</span>
          </h2>
          <p className="text-base md:text-lg max-w-xl mx-auto font-light leading-relaxed transition-colors duration-500" style={{ color: mutedColor }}>
            Nothing slips through the cracks. Every step is automated, integrated, and optimized.
          </p>
        </div>

        <div className="space-y-5">
          {steps.map((step, i) => (
            <div
              key={step.number}
              className="grid grid-cols-1 lg:grid-cols-2 gap-0 rounded-3xl overflow-hidden transition-all duration-500"
              style={{ border: `1px solid ${cardBorder}` }}
            >
              <div className={`relative h-56 md:h-72 ${i % 2 === 1 ? "lg:order-2" : ""}`}>
                <img
                  src={step.image}
                  alt={step.title}
                  className="w-full h-full object-cover object-top"
                />
                <div
                  className="absolute inset-0"
                  style={{
                    background: i % 2 === 0
                      ? `linear-gradient(to right, transparent 40%, ${bg} 100%)`
                      : `linear-gradient(to left, transparent 40%, ${bg} 100%)`,
                  }}
                />
                <div className="absolute top-6 left-6">
                  <span className="text-5xl font-thin" style={{ color: `${step.accentColor}22` }}>
                    {step.number}
                  </span>
                </div>
              </div>

              <div
                className={`flex flex-col justify-center px-8 md:px-12 py-10 transition-colors duration-500 ${i % 2 === 1 ? "lg:order-1" : ""}`}
                style={{ background: contentBg }}
              >
                <div
                  className="w-10 h-10 flex items-center justify-center rounded-xl mb-5"
                  style={{ background: `${step.accentColor}14`, border: `1px solid ${step.accentColor}22` }}
                >
                  <i className={`${step.icon} text-base`} style={{ color: step.accentColor }}></i>
                </div>
                <div className="text-[10px] font-mono mb-2" style={{ color: isDark ? "rgba(255,255,255,0.2)" : "rgba(28,26,34,0.25)" }}>{step.number}</div>
                <h3 className="font-semibold text-xl md:text-2xl mb-3 leading-tight transition-colors duration-500" style={{ color: headlineColor }}>{step.title}</h3>
                <p className="text-sm md:text-base leading-relaxed font-light transition-colors duration-500" style={{ color: mutedColor }}>{step.description}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
